import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { redoToUIMessages } from "../conversion/redo-to-ui-message";
import { mergeEscalationHistory } from "../escalation/intercom-to-uimessage";
import type { AskRedoUIMessage } from "../types";

/**
 * Lists the current user's Ask Redo threads (for the slide-out +
 * full-page history sidebar). 50 most recent, newest first.
 */
export function useAskRedoThreadList() {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const user = useRequiredContext(UserContext);

  return useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return rpcClient.getAskRedoThreads({ userEmail: user.email });
  }, [rpcClient, user.email]);
}

/** Fetches a thread plus, when escalated, its support-team history. */
export async function loadAskRedoThreadHistory(
  rpcClient: RedoMerchantRpcClient,
  threadId: string,
) {
  const { thread } = await rpcClient.getAskRedoThread({ threadId });

  // If the thread has an escalation, fetch its admin + user messages
  // so the reopened conversation includes the full back-and-forth
  // with the support team. Intercom is the source of truth for the
  // escalated turns, so we always merge fresh from there rather than
  // persisting them on the LlmThread.
  if (thread.escalation) {
    const escalationId = thread.escalation.escalationId;
    try {
      const result = await rpcClient.getEscalationMessages({ escalationId });
      return {
        thread,
        escalation: thread.escalation,
        escalationMessages: result,
      };
    } catch (err) {
      // If the escalation messages fail to load, still show the
      // AI thread — the merchant can re-fetch via reload. Log so
      // we can correlate "merchant says reopened thread missing
      // human replies" with the actual RPC failure.
      console.error(
        "[ask-redo] getEscalationMessages failed during thread reopen",
        { escalationId, threadId },
        err,
      );
      return {
        thread,
        escalation: thread.escalation,
        escalationMessages: undefined,
      };
    }
  }

  return { thread, escalation: undefined, escalationMessages: undefined };
}

export type AskRedoThreadHistory = Awaited<
  ReturnType<typeof loadAskRedoThreadHistory>
>;

/** The thread's messages as UI messages, followed by its support-team history. */
export function askRedoThreadHistoryMessages(
  history: AskRedoThreadHistory,
): AskRedoUIMessage[] {
  // AI thread messages don't carry per-message timestamps —
  // `MessageSchema` in `redo-model/agents/llm-messages` has no
  // `createdAt` field and the Mongoose schema doesn't synthesise
  // one. Without those, a true chronological merge with the
  // Intercom escalation history (which DOES have createdAt) is
  // impossible from the client. We concatenate as
  // `[...ai, ...escalation]` and accept the limitation:
  //
  //   - Common case (escalate → support resolves → close →
  //     thread reopened later): renders correctly. AI turns
  //     happened before escalation, escalation messages last.
  //
  //   - Edge case (escalate → close → CONTINUE the same thread
  //     with more AI turns → reopen): the new AI turns render
  //     BEFORE the closed escalation transcript instead of
  //     after. Fixing this requires a server-side schema change
  //     (per-message createdAt OR an index marker on the
  //     escalation pointing at the AI message that triggered
  //     it). Tracked as a follow-up; not a regression from main
  //     — this is the same behaviour the slide-out shipped with.
  const aiMessages = redoToUIMessages(
    history.thread.messages,
    history.thread.pendingApprovals,
    history.thread.seededEmail,
  );
  const escalationMessages = history.escalationMessages
    ? mergeEscalationHistory(
        history.escalationMessages.messages,
        history.escalationMessages.userMessages,
      )
    : [];
  return [...aiMessages, ...escalationMessages];
}

/**
 * Loads a single thread's full message history, converted to
 * `AskRedoUIMessage[]` ready to pass as `initialMessages` to
 * `useAskRedoChat`.
 *
 * `pending` is true only while there's a `threadId` to fetch for — a
 * fresh-thread caller (no `threadId`) gets `{ pending: false,
 * messages: undefined, error: undefined }` immediately so the page
 * can render its empty hero without flashing a spinner.
 *
 * `reload` re-runs the fetch — used by the page's error state so a
 * transient network failure isn't a dead-end for the merchant.
 */
export function useAskRedoThreadHistory(threadId: string | undefined): {
  pending: boolean;
  messages: AskRedoUIMessage[] | undefined;
  messagesDropped: boolean;
  /** Set while a turn runs; `messages` then stop before its reply. */
  activeTurn: { turnId: string } | undefined;
  error: unknown;
  reload: () => void;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { escalation, setEscalation, setTriageThreadId } =
    useEscalationContext();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const load = useLoad(async () => {
    if (!threadId) {
      return undefined;
    }
    return loadAskRedoThreadHistory(rpcClient, threadId);
    // `reloadKey` is part of the deps so bumping it re-fires the
    // loader without changing any of the "real" inputs.
  }, [rpcClient, threadId, reloadKey]);

  // Read via ref in the hydration effect: a direct `escalation` dep
  // would loop (setEscalation always creates a new state object).
  const escalationRef = useRef(escalation);
  useEffect(() => {
    escalationRef.current = escalation;
  }, [escalation]);

  // Seed EscalationContext when we land on a thread with an
  // escalation. The status from getEscalationMessages is fresher than
  // the snapshot on the thread record (which can lag if the human
  // joined/closed between page loads).
  //
  // If the loaded thread has NO escalation, we INTENTIONALLY do not
  // touch the EscalationContext. A previous version of this code
  // called `clearEscalation()` here, which broke the live SSE
  // subscription whenever a merchant with an active escalation
  // browsed to an unrelated thread — every later admin reply on the
  // escalated thread was lost. Diversion correctness is now handled
  // in `AskRedoRpcTransport`: it scopes the human-active divert to
  // `chatId === escalation.triageThreadId`, so sends from this
  // unrelated thread go to the agent and the escalation lives on
  // until it's actually closed.
  useEffect(() => {
    if (!load.value || !threadId) {
      return;
    }
    setTriageThreadId(threadId);
    if (!load.value.escalation) {
      return;
    }
    const freshStatus =
      load.value.escalationMessages?.status ?? load.value.escalation.status;
    const current = escalationRef.current;
    // A closed escalation must not clobber a DIFFERENT live one: the
    // swap aborts its SSE stream, and closed status never reconnects.
    if (
      freshStatus === "closed" &&
      current.escalationId !== null &&
      current.escalationId !== load.value.escalation.escalationId &&
      (current.status === "waiting" || current.status === "human-active")
    ) {
      return;
    }
    // `escalationMessages?.status` (from `getEscalationMessages`) and
    // `escalation.status` (from `ThreadEscalationInfoSchema`) both
    // resolve to `EscalationStatus` ("waiting" | "human-active" |
    // "closed") — a strict subset of `EscalationState.status` which
    // also includes "idle". The union widens implicitly; no type
    // assertion needed. A previous version of this code had
    // `as "idle" | "waiting" | ...` here, which violated CLAUDE.md's
    // ban on assertions-to-pass-builds and would have hidden a real
    // mismatch if the schemas drifted apart.
    setEscalation({
      escalationId: load.value.escalation.escalationId,
      noticeTopic: load.value.escalation.noticeTopic,
      status: freshStatus,
      // Bind the escalation to its triage thread id so the watcher
      // can dispatch live human messages to the right cached chat
      // even if the merchant later opens an unrelated thread.
      triageThreadId: threadId,
      // Intercom conversation id isn't in ThreadEscalationInfo; it
      // gets backfilled by useEscalationNotifications.getEscalation
      // on first need.
      intercomConversationId: null,
    });
  }, [load.value, threadId, setEscalation, setTriageThreadId]);

  const messages = useMemo(
    () => (load.value ? askRedoThreadHistoryMessages(load.value) : undefined),
    [load.value],
  );

  // Surfaced as a banner above the composer instead of a synthetic
  // assistant turn, so a load skew never reads as the AI speaking.
  const messagesDropped = load.value?.thread.messagesDropped ?? false;

  // No threadId → nothing to load; mask `useLoad`'s pending=true
  // flicker on mount so the caller can render its empty state.
  if (!threadId) {
    return {
      pending: false,
      messages: undefined,
      messagesDropped: false,
      activeTurn: undefined,
      error: undefined,
      reload,
    };
  }

  return {
    pending: load.pending,
    messages,
    messagesDropped,
    activeTurn: load.value?.thread.activeTurn,
    error: load.error,
    reload,
  };
}
